import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import React, { useState, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import { Page, Header, Alert } from 'components';
import { Grid, Tabs, Tab, CardContent, Autocomplete, createFilterOptions, TextField, Card, Button } from '@mui/material';
import { enqueueSnackbar } from 'actions';
import _ from 'underscore';
import TableViewIcon from '@mui/icons-material/TableView';
import EmailIcon from '@mui/icons-material/Email';

import SiteReportTable from './components/SiteReportTable';

import { GetLogsLastUpdate } from '../actions/UploadLogsActions.js';
import { GetSiteReport, GetSiteList, SetSelectedSite, GetSiteReportEmail, GetSiteReportExcel, GetSiteReportEmailAcceptance } from '../actions/SiteReportActions.js';

const useStyles = makeStyles((theme) => ({
	root: {
		maxWidth: '100%',
		margin: '0 auto',
		padding: theme.spacing(3),
	},
	tabs: {
		marginTop: theme.spacing(3),
	},
	alert: {
		marginTop: theme.spacing(3),
	},
	content: {
		marginTop: theme.spacing(3),
	},
	container: {
		marginTop: theme.spacing(3),
	}
}));

const tabs = [
	{ value: 'Summary', label: 'Summary' },
	{ value: 'RET Information', label: 'RET Information' },
	{ value: 'Alarms', label: 'Alarms' },
	{ value: 'Configuration Check 2G', label: 'Configuration Check 2G' },
	{ value: 'Configuration Check 3G', label: 'Configuration Check 3G' },
	{ value: 'Configuration Check 4G', label: 'Configuration Check 4G' },
	{ value: 'Configuration Check 5G', label: 'Configuration Check 5G' },
	{ value: 'Configuration Check NBIoT', label: 'Configuration Check NBIoT' }
];

const SiteReport = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const [tab, setTab] = useState('Summary');
	const user = useSelector(state => state.session.user);
	const logsLastUpdate = useSelector((state) => state.uploadLogsReducer.logsLastUpdate);
	const siteReport = useSelector((state) => state.siteReportReducer.siteReport);
	const siteList = useSelector((state) => state.siteReportReducer.siteList);
	const selectedSite = useSelector((state) => state.siteReportReducer.selectedSite);
	const siteReportTabs = useSelector((state) => state.siteReportReducer.siteReportTabs);

	useEffect(() => {
		dispatch(GetSiteList());
		dispatch(GetLogsLastUpdate());
	}, []);

	const handleTabsChange = (event, value) => {
		setTab(value);
	};

	const filterOptions = createFilterOptions({
		limit: 50
	});

	const _handleSiteChange = (value) => {
		dispatch(SetSelectedSite(value));
		dispatch(GetSiteReport(value));
	};

	const _onExcelExportClick = () => {
		dispatch(GetSiteReportExcel(selectedSite));
	};

	const _onEmailExportClick = () => {
		dispatch(GetSiteReportEmail(selectedSite, user.id));
	};

	const _onEmailAcceptanceClick = () => {
		dispatch(GetSiteReportEmailAcceptance(selectedSite, user.id));
	};

	return (
		<Page className={classes.root} title="Monitoring">
			<Header module="Monitoring" view="Site Report" />
			<Alert
				className={classes.container}
				variant="info"
				message={_.isUndefined(logsLastUpdate) ? 'Logs date unavailable!' : logsLastUpdate}
			/>
			<Card className={classes.container} container spacing={3}>
				<CardContent>
					<Grid container spacing={2}>
						<Grid item lg={3} xs={3} spacing={3}>
							<Autocomplete
								id="siteId"
								name="siteId"
								hintText="Site Id"
								options={siteList || []}
								value={{ value: selectedSite || '', text: selectedSite || '' }}
								getOptionLabel={(option) => `${option.text}`}
								renderInput={(params) => <TextField {...params} label="Site ID" variant="outlined" />}
								filterOptions={filterOptions}
								onChange={(event, value) => { _handleSiteChange(value.value); }}
							/>
						</Grid>
						<Grid item lg={6} xs={6} spacing={3}>
							<Button
								className={classes.agreeButton}
								color="primary"
								variant="contained"
								size="medium"
								startIcon={<EmailIcon />}
								onClick={_onEmailAcceptanceClick}
							>
								Email Acceptance
							</Button>
							<Button
								className={classes.agreeButton}
								color="primary"
								variant="contained"
								size="medium"
								startIcon={<EmailIcon />}
								onClick={_onEmailExportClick}
							>
								Email Report
							</Button>
							<Button
								className={classes.agreeButton}
								color="success"
								variant="contained"
								size="medium"
								startIcon={<TableViewIcon />}
								onClick={_onExcelExportClick}
							>
								Excel Report
							</Button>
						</Grid>
					</Grid>
				</CardContent>
				<Tabs
					className={classes.tabs}
					onChange={handleTabsChange}
					scrollButtons="auto"
					value={tab}
					variant="scrollable"
				>
					{tabs.map((item) => (
						<Tab key={item.value} label={item.label} value={item.value} />
					))}
				</Tabs>
			</Card>
			<div className={classes.content}>
				{tab === 'Summary' && (
					<SiteReportTable
						className={classes.results}
						name="Site Summary"
						structure={siteReport.length > 0 ? siteReport[siteReport.findIndex(x => x.TableName === 'Site Summary')].TableStructure : []}
						data={siteReport.length > 0 ? siteReport[siteReport.findIndex(x => x.TableName === 'Site Summary')].TableData : []}
					/>
				) }
				{tab === 'Summary' && (<br />)}
				{tab === 'Summary' && (
					<SiteReportTable
						className={classes.results}
						name="Commissioning Information"
						structure={siteReport.length > 0 ? siteReport[siteReport.findIndex(x => x.TableName === 'Commissioning Information')].TableStructure : []}
						data={siteReport.length > 0 ? siteReport[siteReport.findIndex(x => x.TableName === 'Commissioning Information')].TableData : []}
					/>
				) }
				{tab === 'RET Information' && (
					<SiteReportTable
						className={classes.results}
						name="RET Information"
						structure={siteReport.length > 0 ? siteReport[siteReport.findIndex(x => x.TableName === 'RET Information')].TableStructure : []}
						data={siteReport.length > 0 ? siteReport[siteReport.findIndex(x => x.TableName === 'RET Information')].TableData : []}
					/>
				) }
				{tab === 'Alarms' && (
					<SiteReportTable
						className={classes.results}
						name="Alarms"
						structure={siteReport.length > 0 ? siteReport[siteReport.findIndex(x => x.TableName === 'Alarms')].TableStructure : []}
						data={siteReport.length > 0 ? siteReport[siteReport.findIndex(x => x.TableName === 'Alarms')].TableData : []}
					/>
				) }
				{tab === 'Configuration Check 2G' && (
					<SiteReportTable
						className={classes.results}
						name="Configuration Check 2G"
						structure={siteReport.length > 0 ? siteReport[siteReport.findIndex(x => x.TableName === 'Configuration Check 2G')].TableStructure : []}
						data={siteReport.length > 0 ? siteReport[siteReport.findIndex(x => x.TableName === 'Configuration Check 2G')].TableData : []}
					/>
				) }
				{tab === 'Configuration Check 3G' && (
					<SiteReportTable
						className={classes.results}
						name="Configuration Check 3G"
						structure={siteReport.length > 0 ? siteReport[siteReport.findIndex(x => x.TableName === 'Configuration Check 3G')].TableStructure : []}
						data={siteReport.length > 0 ? siteReport[siteReport.findIndex(x => x.TableName === 'Configuration Check 3G')].TableData : []}
					/>
				) }
				{tab === 'Configuration Check 4G' && (
					<SiteReportTable
						className={classes.results}
						name="Configuration Check 4G"
						structure={siteReport.length > 0 ? siteReport[siteReport.findIndex(x => x.TableName === 'Configuration Check 4G')].TableStructure : []}
						data={siteReport.length > 0 ? siteReport[siteReport.findIndex(x => x.TableName === 'Configuration Check 4G')].TableData : []}
					/>
				) }
				{tab === 'Configuration Check 5G' && (
					<SiteReportTable
						className={classes.results}
						name="Configuration Check 5G"
						structure={siteReport.length > 0 ? siteReport[siteReport.findIndex(x => x.TableName === 'Configuration Check 5G')].TableStructure : []}
						data={siteReport.length > 0 ? siteReport[siteReport.findIndex(x => x.TableName === 'Configuration Check 5G')].TableData : []}
					/>
				) }
				{tab === 'Configuration Check NBIoT' && (
					<SiteReportTable
						className={classes.results}
						name="Configuration Check NBIoT"
						structure={siteReport.length > 0 ? siteReport[siteReport.findIndex(x => x.TableName === 'Configuration Check NBIoT')].TableStructure : []}
						data={siteReport.length > 0 ? siteReport[siteReport.findIndex(x => x.TableName === 'Configuration Check NBIoT')].TableData : []}
					/>
				) }
			</div>
		</Page>
	);
};

export default SiteReport;
